import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  Entities_Team_MovePlayers_Player,
  Entities_Team_MovePlayers_Team,
  PlayerId,
} from '@suntex.fim/api-client';
import { $SpecialGradeLevel } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';

import { useSchoolContext } from '../context';

export interface UseSchoolMovePlayersResult {
  teams: Entities_Team_MovePlayers_Team[];
  isLoading: boolean;
  reload: () => void;
}

/**
 * Loads the move-players board (teams + players) for the school in the
 * surrounding `SchoolProvider`. Skipped when no school id is available.
 */
export function useSchoolMovePlayers(): UseSchoolMovePlayersResult {
  const { schoolId } = useSchoolContext();
  const { api } = useFramework();
  const queryClient = useQueryClient();

  const { data = [], isLoading } = useQuery({
    queryKey: ['school', 'movePlayers', schoolId],
    queryFn: async () => {
      const response = await api.get_School_MovePlayers({
        schoolId: schoolId!,
      });
      return response.teams.map(
        (t): Entities_Team_MovePlayers_Team => ({
          ...t,
          grade: $SpecialGradeLevel.from(t.grade),
          players: t.players.map(
            (p): Entities_Team_MovePlayers_Player => ({
              ...p,
              id: p.id as PlayerId,
            }),
          ),
        }),
      );
    },
    enabled: schoolId !== null,
  });

  const reload = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['school', 'movePlayers', schoolId],
    });
  }, [queryClient, schoolId]);

  return { teams: data, isLoading, reload };
}
